import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { ExternalLinkButton } from '@/components/base/buttons/button';
import { Card } from '@/components/base/card/card';
import { teamPlanSummaryQueryOptions } from '@/routes/researchers/_authenticated/projects/$projectId/-launch/team-pricing-version';
import { formatChargeCents } from '@/utils/currency';

import { MERCHANT_FEE_PERCENT, withMerchantFee } from './-merchant-fee';
import { PlanActions } from './-plan-actions';
import { planExpiration } from './-plan-management';

export const Route = createFileRoute(
  '/researchers/_authenticated/billing/plans',
)({
  component: BillingPlansPage,
  head: () => ({ meta: [{ title: t`Plans` }] }),
});

function BillingPlansPage() {
  const { user, activeDashboard, env } = Route.useRouteContext();
  const teamId = activeDashboard.team ?? '';
  const organizationId = activeDashboard.organization ?? '';
  const classicHref = `${env.WEBSITE_URL}/dashboard/organizations/${organizationId}/teams/${teamId}/billing/plans`;
  const { data, isPending, isError } = useQuery({
    ...teamPlanSummaryQueryOptions(teamId, user.id),
    enabled: !!teamId,
  });
  const plan = data?.plan;
  const rateCard = plan?.rateCard;
  const agreement = plan?.agreement;
  const expiration = plan ? planExpiration(plan) : undefined;
  const renewalFeeNote =
    plan && plan.renewalCost > 0
      ? t`plus ${MERCHANT_FEE_PERCENT}% processing fee (${formatChargeCents(withMerchantFee(plan.renewalCost).feeCents)})`
      : undefined;

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-display-xs font-semibold text-primary">
          <Trans>Plans</Trans>
        </h1>
        <ExternalLinkButton
          color="secondary"
          size="sm"
          href={classicHref}
          target="_blank"
        >
          <Trans>Classic billing</Trans>
        </ExternalLinkButton>
      </div>
      {isPending && (
        <p role="status" className="text-sm text-secondary">
          <Trans>Loading plan…</Trans>
        </p>
      )}
      {isError && (
        <p role="alert" className="text-sm text-error-primary">
          <Trans>
            Could not load plan. You can manage it in classic billing.
          </Trans>
        </p>
      )}
      {!isPending && !isError && !plan && (
        <p className="text-sm text-secondary">
          <Trans>No active plan is available for this team.</Trans>
        </p>
      )}
      {plan && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-md font-semibold text-primary">
                {plan.pricingVersion === 3 ? t`Agreement` : t`Current plan`}
              </h2>
              {plan.pausedAt && (
                <span className="rounded-full bg-success-secondary px-2 py-1 text-xs text-success-primary">
                  <Trans>Plan on pause</Trans>
                </span>
              )}
            </div>
            <dl className="space-y-3 text-sm">
              {plan.pricingVersion === 3 ? (
                <>
                  <PlanRow
                    label={t`Commitment`}
                    value={
                      agreement ? formatChargeCents(agreement.commitment) : '—'
                    }
                  />
                  <PlanRow
                    label={t`Owed`}
                    value={
                      agreement
                        ? formatChargeCents(agreement.commitmentOwed)
                        : '—'
                    }
                  />
                  <PlanRow
                    label={t`Expiration`}
                    value={
                      expiration
                        ? new Date(expiration).toLocaleDateString()
                        : '—'
                    }
                  />
                  {plan.renewalCost > 0 && (
                    <PlanRow
                      label={t`Renewal cost`}
                      value={formatChargeCents(plan.renewalCost)}
                      note={renewalFeeNote}
                    />
                  )}
                </>
              ) : (
                <>
                  <PlanRow label={t`Plan`} value={plan.name} />
                  <PlanRow
                    label={t`Participant type`}
                    value={plan.participantType.toUpperCase()}
                  />
                  <PlanRow
                    label={t`Renewal cost`}
                    value={formatChargeCents(plan.renewalCost)}
                    note={renewalFeeNote}
                  />
                  <PlanRow
                    label={t`Ends on`}
                    value={
                      plan.endsAt
                        ? new Date(plan.endsAt).toLocaleDateString()
                        : '—'
                    }
                  />
                </>
              )}
              <PlanRow
                label={t`Renewing`}
                value={plan.autoRenew ? t`Yes` : t`No`}
              />
            </dl>
          </Card>
          {plan.pricingVersion === 3 && rateCard?.matrix && (
            <Card className="space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-md font-semibold text-primary">
                  <Trans>Rate card</Trans>
                </h2>
                {agreement?.baseDiscount != null &&
                  agreement.baseDiscount > 0 && (
                    <span className="rounded-full bg-success-secondary px-2 py-1 text-xs text-success-primary">
                      {agreement?.baseDiscount}% <Trans>off</Trans>
                    </span>
                  )}
              </div>
              <table className="w-full text-sm text-primary">
                <caption className="sr-only">
                  <Trans>Recruitment prices by study type and audience</Trans>
                </caption>
                <thead>
                  <tr>
                    <th scope="col" className="py-2 text-left">
                      <Trans>Method</Trans>
                    </th>
                    <th scope="col" className="py-2 text-right">
                      B2C
                    </th>
                    <th scope="col" className="py-2 text-right">
                      B2B
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <RateRow
                    label={t`Moderated`}
                    b2c={rateCard.matrix.b2c.oneOnOne}
                    b2b={rateCard.matrix.b2b.oneOnOne}
                  />
                  <RateRow
                    label={t`Unmoderated`}
                    b2c={rateCard.matrix.b2c.unmoderatedStudy}
                    b2b={rateCard.matrix.b2b.unmoderatedStudy}
                  />
                  <RateRow
                    label={t`Survey`}
                    b2c={rateCard.matrix.b2c.survey}
                    b2b={rateCard.matrix.b2b.survey}
                  />
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}
      {data && plan && teamId && organizationId && (
        <PlanActions
          teamId={teamId}
          organizationId={organizationId}
          userId={user.id}
          summary={data}
          isEmailVerified={user.isEmailVerified}
          isGlobalAdmin={user.roles.includes('admin')}
          key={`${user.id}-${teamId}-${plan.teamPlanId}`}
        />
      )}
    </>
  );
}

function PlanRow({
  label,
  value,
  note,
}: {
  label: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-secondary">{label}</dt>
      <dd className="text-right text-primary">
        {value}
        {note && <span className="block text-xs text-secondary">{note}</span>}
      </dd>
    </div>
  );
}

function RateRow({
  label,
  b2c,
  b2b,
}: {
  label: string;
  b2c: number;
  b2b: number;
}) {
  return (
    <tr>
      <th scope="row" className="py-2 text-left font-normal">
        {label}
      </th>
      <td className="py-2 text-right">{formatChargeCents(b2c)}</td>
      <td className="py-2 text-right">{formatChargeCents(b2b)}</td>
    </tr>
  );
}
